import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import { useGetTrucksQuery } from '../../../redux/slices/truckSlice';
import usePagination from '../../../hooks/usePagination';
import useSearchFilters from '../../../hooks/useSearchFilters';

// Components
import QuoteCta from '../content/QuoteCta';
import FeaturedTrucks from '../FeaturedTrucks';
import TruckTease from '../TruckTease';
import FeaturedPods from '../FeaturedPods';
import CateringCta from '../content/CateringCta';
import Paginate from '../../shared/Paginate';
import Meta from '../../shared/Meta';
import CateringFaq from '../faqs/CateringFaq';
import SearchFilters from '../search/SearchFilters';

// Styles
import paginateRoundTheme from '../../shared/Paginate-round.module.postcss';
import styles from './CategoryPage.module.postcss';
import SimpleCta from '../content/SimpleCta';

const featuredOptions = { page_size: 3, featured: true };

// Cuisine page URL mapping for cross-links
// `label` is the singular, mid-phrase form used in copy ("Taco trucks", not "Tacos Trucks")
const CUISINE_PAGES = [
  { name: 'Tacos', label: 'Taco', url: '/taco-trucks', emoji: '🌮', cateringUrl: '/food-truck-catering/taco-catering' },
  { name: 'BBQ', label: 'BBQ', url: '/bbq-food-trucks', emoji: '🍖', cateringUrl: '/food-truck-catering/bbq-catering' },
  { name: 'Pizza', label: 'Pizza', url: '/pizza-food-trucks', emoji: '🍕', cateringUrl: '/food-truck-catering/pizza-catering' },
  { name: 'Desserts', label: 'Dessert', url: '/dessert-food-trucks', emoji: '🍦', cateringUrl: '/food-truck-catering/dessert-catering' },
  { name: 'Breakfast', label: 'Breakfast', url: '/breakfast-food-trucks', emoji: '🥞', cateringUrl: '/food-truck-catering/breakfast-catering' },
  { name: 'Burgers', label: 'Burger', url: '/burger-food-trucks', emoji: '🍔', cateringUrl: '/food-truck-catering/burger-catering' },
  { name: 'Sandwiches', label: 'Sandwich', url: '/sandwich-food-trucks', emoji: '🥪' },
  { name: 'Healthy', label: 'Healthy', url: '/healthy-food-trucks', emoji: '🥗' },
  { name: 'Gyros', label: 'Gyro', url: '/gyro-food-trucks', emoji: '🥙' },
  { name: 'Asian', label: 'Asian', url: '/asian-food-trucks', emoji: '🥡', cateringUrl: '/food-truck-catering/asian-catering' },
];

function CategoryPage({ foodCategory, foodName, seoTitle, seoDescription, seoCanonical, MainText, SecondaryText, Faq, cateringUrl }) {
  const { searchFilters, setFilters: rawSetFilters } = useSearchFilters({ sendToSearchPage: true });

  // When any filter is applied on a category page, carry the cuisine context
  // into /search so the map + time-based results work correctly there.
  const setFilters = useCallback((params) => {
    rawSetFilters({
      ...(foodCategory && { food_categories: foodCategory }),
      ...params,
    });
  }, [rawSetFilters, foodCategory]);
  const { city_name, vehicle_type, base_url } = useContext(ConfigContext);
  const location = useLocation();

  const featuredTrucks = {
    featured: true,
    ...(foodCategory && { food_categories: foodCategory })
  };

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const page = urlParams.get('page') || 1;
    setCurrentPage(page);
  }, [location.search]);

  const { data = {} } = useGetTrucksQuery({
    ...(foodCategory && { food_categories: foodCategory }),
    random_order: true,
    page_size: 9,
    page: currentPage,
  });
  const trucks = data.trucks || [];
  const pagination = data.pagination;

  const { paginationProps, showPagination } = usePagination({
    pagination,
    options: { theme: paginateRoundTheme, previousLabel: '<', nextLabel: '>' }
  });

  const vehicleLowerCase = vehicle_type.toLowerCase();
  const cuisine = CUISINE_PAGES.find((c) => c.name === foodName);
  const cuisineLabel = cuisine?.label || foodName;
  const truckType = cuisineLabel ? `${cuisineLabel} food ${vehicleLowerCase}` : `Food ${vehicleLowerCase}`;

  // Build structured data for this cuisine page
  const structuredData = useMemo(() => {
    const sd = {
      breadcrumbs: {
        listItems: [
          { '@type': 'ListItem', position: 2, name: 'Food Trucks', item: `${base_url}/explore` },
          { '@type': 'ListItem', position: 3, name: `${foodName} Trucks`, item: `${base_url}${seoCanonical || location.pathname}` },
        ],
      },
    };

    if (trucks.length > 0) {
      sd.itemList = {
        name: `${truckType}s in ${city_name}`,
        description: seoDescription,
        items: trucks.map((truck) => ({
          name: truck.name,
          url: `${base_url}/food-trucks/${truck.id}`,
          servesCuisine: foodName,
          image: truck.featured_photo,
        })),
      };
    }

    return sd;
  }, [trucks, foodName, city_name, truckType, base_url, seoDescription, seoCanonical, location.pathname]);

  // Cross-links: all cuisines except the current one
  const crossLinks = CUISINE_PAGES.filter((c) => c.name !== foodName);

  // Find the matching catering URL for this cuisine
  const matchingCateringUrl = cateringUrl || cuisine?.cateringUrl;

  return (
    <div>
      <Meta
        title={seoTitle || `${truckType}s in ${city_name}`}
        structuredData={structuredData}
        type='website'
        description={seoDescription || `${truckType}s in ${city_name}: menus, schedules, and catering quotes.`}
        canonical={seoCanonical}
      />

      {/* Breadcrumb navigation */}
      <div className='Section u-pb0'>
        <nav aria-label='Breadcrumb'>
          <ol className={styles.Breadcrumbs}>
            <li><Link to='/'>Home</Link></li>
            <li><Link to='/explore'>Food {vehicleLowerCase}s</Link></li>
            <li>{cuisineLabel ? `${cuisineLabel} ${vehicleLowerCase}s` : 'All'}</li>
          </ol>
        </nav>
      </div>

      <SearchFilters
        filters={searchFilters}
        setFilters={setFilters}
        noMapToggle
      />

      <div className='Section u-pt05 u-pb0'>
        <QuoteCta />
      </div>

      <FeaturedTrucks
        title='Sponsored results'
        showMenuLinks
        options={featuredOptions}
      />

      <section className='Section'>
        { MainText }
      </section>

      <FeaturedTrucks noTitle options={featuredTrucks} />

      <section className='Section'>
        <div className={styles.TrucksGrid}>
          { trucks.map((truck) =>
            <TruckTease truck={truck} key={'truck-' + truck.id} />
          )}
        </div>
        { showPagination && <Paginate {...paginationProps} /> }
      </section>

      <section className='Section'>
        <SimpleCta
          text={`Don’t see your food ${vehicleLowerCase}?`}
          link='/vendor/register'
          linkTitle='Create your vendor profile'
        />
      </section>

      <section className='Section--extraPadding'>
        { SecondaryText }
      </section>

      {/* Catering cross-link */}
      { matchingCateringUrl &&
        <section className='Section'>
          <div className={styles.CateringCta}>
            <p>Planning an event? {cuisineLabel} {vehicleLowerCase}s in {city_name} can send you catering quotes.</p>
            <Link to={matchingCateringUrl} className='Button'>
              Get catering quotes
            </Link>
          </div>
        </section>
      }

      <section className='Section'>
        <FeaturedPods />
      </section>

      <section className='Section--extraPadding'>
        { Faq || <CateringFaq /> }
      </section>

      {/* Cross-links to other cuisine pages */}
      <section className='Section'>
        <h3 className='u-textCenter u-phosphate'>More cuisines</h3>
        <div className={styles.CrossLinks}>
          { crossLinks.map((link) => (
            <Link key={link.url} to={link.url}>
              {link.emoji} {link.label} {vehicleLowerCase}s
            </Link>
          ))}
        </div>
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

CategoryPage.propTypes = {
  foodCategory: PropTypes.string,
  foodName: PropTypes.string,
  seoTitle: PropTypes.string,
  seoDescription: PropTypes.string,
  seoCanonical: PropTypes.string,
  MainText: PropTypes.node.isRequired,
  SecondaryText: PropTypes.node.isRequired,
  Faq: PropTypes.node,
  cateringUrl: PropTypes.string,
};

export default CategoryPage;
